import { createColumnHelper } from "@tanstack/react-table";
import { Loader } from "semantic-ui-react";

import { ReactTable } from "components";
import { Recall } from "models";
import "modules/RecallsDashboard/components/RecallsDashboardTable/RecallsDashboardTable.scss";
import { useRecallsDashboardFilterHandlers, useRecallsDashboardFilters } from "modules/RecallsDashboard/hooks";
import { useSearchRecalls } from "modules/RecallsDashboard/service";
import { formatDate } from "util/dateHelpers";
import { DATE_FORMATS_FNS } from "util/dateHelpers";

export const RecallsDashboardTable = () => {
  const {
    data: { page: currentPage, page_size: pageSize }
  } = useRecallsDashboardFilters();
  const { data, isFetching } = useSearchRecalls();
  const { handlePageSizeChange, handlePaginationChange } = useRecallsDashboardFilterHandlers();

  if (isFetching)
    return (
      <div className="RecallsDashboardTable__table-loader">
        <Loader active inline />
      </div>
    );

  if (!data?.recalls?.length) return <div className="RecallsDashboardTable__no-data">No Data</div>;

  const { recalls: recalls, nb_items, nb_pages } = data;
  const tablePagination = nb_pages > 0 ? { pageIndex: currentPage - 1, pageSize } : undefined;
  const columnHelper = createColumnHelper<Recall>();

  const renderDate = (date: string | null) => <div>{date ? formatDate(date, DATE_FORMATS_FNS.monthDayYear) : null}</div>;

  const columns = [
    columnHelper.accessor("campaign_code", {
      header: "Campaign Code",
      cell: info => info.renderValue()
    }),

    columnHelper.accessor("campaign_name", {
      header: "Campaign Name",
      cell: info => info.renderValue()
    }),

    columnHelper.accessor("campaign_type", {
      header: () => <div>Campaign Type&nbsp;Code</div>,
      cell: info => info.renderValue()
    }),

    columnHelper.accessor(row => row.start_date, {
      id: "start_date",
      header: "Start Date",
      cell: info => <div>{renderDate(info.getValue())}</div>
    }),

    columnHelper.accessor(row => row.close_date, {
      id: "close_date",
      header: "Close Date",
      cell: info => renderDate(info.getValue())
    }),

    columnHelper.accessor(row => row.release_date, {
      id: "release_date",
      header: "Release Date",
      cell: info => renderDate(info.getValue())
    }),

    columnHelper.accessor("vin", {
      header: "VIN",
      cell: info => info.renderValue()
    }),

    columnHelper.accessor("status", {
      header: "Status",
      cell: info => info.renderValue()
    }),

    columnHelper.accessor("buy_off_company", {
      header: "Buy Off Company",
      cell: info => info.renderValue()
    }),

    columnHelper.accessor("approval_company", {
      header: "Approval Company",
      cell: info => info.renderValue()
    }),

    columnHelper.accessor("customer_id", {
      header: "Customer",
      cell: info => info.renderValue()
    }),

    columnHelper.accessor("customer_en", {
      header: "Customer EN",
      cell: info => info.renderValue()
    }),

    columnHelper.accessor("country", {
      header: "Country Region",
      cell: info => info.renderValue()
    }),

    columnHelper.accessor("vehicle_series", {
      header: "Vehicle Series",
      cell: info => info.renderValue()
    }),

    columnHelper.accessor("claim_order", {
      header: "Claim Order",
      cell: info => info.renderValue()
    }),

    columnHelper.accessor(row => row.repair_end_date, {
      id: "repair_end_date",
      header: "Repair End Date",
      cell: info => renderDate(info.getValue())
    }),

    columnHelper.accessor(row => row.approval_date, {
      id: "approval_date",
      header: "Approval Date",
      cell: info => renderDate(info.getValue())
    })
  ];

  return (
    <div className="RecallsDashboardTable__table-container">
      <ReactTable
        pageCount={nb_pages}
        pageSize={pageSize}
        totalItems={nb_items}
        pagination={tablePagination}
        onChangePageSize={handlePageSizeChange}
        onPaginationChange={handlePaginationChange}
        columns={columns}
        data={recalls}
        renderEmptyRow={!recalls?.length}
        emptyRowMessage="No data"
      />
    </div>
  );
};
